
import { Component, Prop, Vue } from 'vue-property-decorator'
import * as XLSX from 'xlsx'

import { Customer, Program } from '@/models'
import { CSV_HEADERS, DotationOrderForm } from '../types/dotationOrderForm'
import {
  createEmptyDotationCsvRow,
  CsvHeader,
  DotationCsvRow,
  parseDotationCsvFile,
  rowsToCsvText,
  rowsToExcelBlob,
  validateDotationRow
} from '../utils/csvImport'
import CsvDataTable from './CsvDataTable.vue'

@Component({
  name: 'StepDotationEntry',
  components: { CsvDataTable }
})
export default class extends Vue {
  @Prop({ required: true }) form!: DotationOrderForm

  programs: Program[] = []
  programsLoading = false
  customers: Customer[] = []
  customersLoading = false
  typeSearch: 'email' | 'lastname' | 'firstname' = 'email'
  csvParsing = false
  csvRows: DotationCsvRow[] = []
  private originalCsvSnapshot = ''

  get hasSingleProgram(): boolean {
    return this.programs.length === 1
  }

  get selectedProgramName(): string {
    return this.programs.find(program => program.id === this.form.programId)?.name || ''
  }

  get beneficiaryCount(): number {
    if (this.form.distributionMode === 'single') return this.form.customerId ? 1 : 0
    if (this.form.distributionMode === 'csv') return this.csvRows.length
    return 0
  }

  get totalDotationAmount(): number {
    if (this.form.distributionMode === 'single') return Number(this.form.amount) || 0
    if (this.form.distributionMode === 'csv') {
      return this.csvRows.reduce((total, row) => total + (Number(row.amount) || 0), 0)
    }
    return 0
  }

  get distributionLabel(): string {
    if (this.form.distributionMode === 'single') {
      return this.$t('newDotationOrder.distribution.single.title') as string
    }
    if (this.form.distributionMode === 'csv') {
      return this.$t('newDotationOrder.distribution.csv.title') as string
    }
    return '—'
  }

  get orderTypeLabel(): string {
    if (this.form.cmdType === 'physical') {
      return this.$t('newDotationOrder.orderType.physical.title') as string
    }
    if (this.form.cmdType === 'virtual') {
      return this.$t('newDotationOrder.orderType.virtual.title') as string
    }
    return this.$t('newDotationOrder.orderType.standard.title') as string
  }

  get natureLabel(): string {
    if (this.form.nature === 'classic') {
      return this.$t('newDotationOrder.nature.classic.title') as string
    }
    if (this.form.nature === 'urssaf') {
      return this.$t('newDotationOrder.nature.urssaf.title') as string
    }
    return '—'
  }

  get csvHeadersLabel(): string {
    return CSV_HEADERS.join(';')
  }

  get hasCsvRows(): boolean {
    return this.csvRows.length > 0
  }

  get csvErrorSummary(): string[] {
    const t = this.$t.bind(this) as any
    const summary: string[] = []

    this.csvRows.forEach((row, index) => {
      CSV_HEADERS.forEach(header => {
        const message = row.fieldErrors[header]
        if (!message) return

        summary.push(t('newDotationOrder.entry.csvTable.rowError', {
          line: index + 1,
          field: t(`newDotationOrder.entry.csvTable.headers.${header}`),
          message
        }))
      })
    })

    return summary
  }

  /** True only when the table content genuinely differs from what was imported (an edit that was reverted no longer counts). */
  get csvIsModified(): boolean {
    if (!this.csvRows.length) return false
    return rowsToCsvText(this.csvRows) !== this.originalCsvSnapshot
  }

  get showCsvModifiedPrompt(): boolean {
    return this.csvIsModified && !this.form.csvHasErrors
  }

  get scheduledAtDate(): string {
    return this.form.scheduledAt
  }

  set scheduledAtDate(value: string) {
    this.form.scheduledAt = value || ''
  }

  created(): void {
    this.loadPrograms()
  }

  async loadPrograms(): Promise<void> {
    this.programsLoading = true
    try {
      const { data } = await Program.page(1).per(999).all()
      this.programs = data
      if (this.programs.length === 1) {
        this.form.programId = this.programs[0].id ?? ''
      }
    } catch (error) {
      this.$notify({
        title: String(this.$t('newDotationOrder.notification.errorTitle')),
        message: String(this.$t('newDotationOrder.entry.loadProgramsError')),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.programsLoading = false
    }
  }

  async searchCustomers(query: string): Promise<void> {
    if (!query || query.length < 2) {
      this.customers = []
      return
    }

    this.customersLoading = true
    try {
      const { data } = await Customer
        .where({ [this.typeSearch]: { prefix: query } })
        .page(1)
        .per(20)
        .all()
      this.customers = data
    } finally {
      this.customersLoading = false
    }
  }

  onCustomerChange(customerId: string): void {
    const customer = this.customers.find(item => item.id === customerId)
    this.form.customerLabel = customer
      ? `${customer.firstname} ${customer.lastname} - ${customer.email}`
      : ''
  }

  downloadCsvTemplate(): void {
    const content = [
      CSV_HEADERS.join(';'),
      'Jean;Dupont;;jean.dupont@example.com;50;'
    ].join('\n')
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = 'multi_choice_card_order_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  downloadExcelTemplate(): void {
    const rows = [
      [...CSV_HEADERS],
      ['Jean', 'Dupont', '', 'jean.dupont@example.com', '50', '']
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders')
    XLSX.writeFile(workbook, 'multi_choice_card_order_template.xlsx')
  }

  clearCsv(): void {
    this.form.csvBlob = ''
    this.form.csvFileName = ''
    this.form.csvHasErrors = false
    this.csvRows = []
    this.originalCsvSnapshot = ''
  }

  async onCsvFileChange(file: any): Promise<void> {
    const rawFile: File | undefined = file?.raw
    if (!rawFile) return

    this.csvParsing = true
    try {
      const rows = await parseDotationCsvFile(rawFile)
      if (!rows.length) throw new Error('empty file')

      this.csvRows = rows
      this.form.csvFileName = rawFile.name
      this.originalCsvSnapshot = rowsToCsvText(rows)
      this.syncCsvBlob()
    } catch (error) {
      this.clearCsv()
      this.$notify({
        title: String(this.$t('newDotationOrder.notification.errorTitle')),
        message: String(this.$t('newDotationOrder.entry.csvReadError')),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.csvParsing = false
    }
  }

  addCsvRow(): void {
    this.csvRows.push(createEmptyDotationCsvRow())
    this.syncCsvBlob()
  }

  deleteCsvRow(row: DotationCsvRow): void {
    const index = this.csvRows.indexOf(row)
    if (index !== -1) this.csvRows.splice(index, 1)
    this.syncCsvBlob()
  }

  handleCsvTableChange(): void {
    this.syncCsvBlob()
  }

  downloadModifiedExcel(): void {
    const blob = rowsToExcelBlob(this.csvRows)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = (this.form.csvFileName || 'commande').replace(/\.(csv|xlsx|xls)$/i, '') + '_modifie.xlsx'
    link.click()
    URL.revokeObjectURL(url)

    // The downloaded file now reflects the table, so it becomes the new baseline for comparison.
    this.originalCsvSnapshot = rowsToCsvText(this.csvRows)
  }

  /** Called by the parent right before validating, so a still-focused cell's edit isn't missed. */
  refreshCsvValidation(): void {
    if (this.form.distributionMode === 'csv') this.syncCsvBlob()
  }

  /** Injects a row-level error returned by the backend (see App::MultiChoiceCardOrder::ParseCsv) into the table. */
  applyBackendRowError(rowNumber: number, field: CsvHeader | undefined, message: string): boolean {
    const row = this.csvRows[rowNumber - 1]
    if (!row) return false

    if (field) row.fieldErrors = { ...row.fieldErrors, [field]: message }
    this.form.csvHasErrors = true
    return true
  }

  private syncCsvBlob(): void {
    const t = this.$t.bind(this) as any
    this.csvRows.forEach(row => {
      row.fieldErrors = validateDotationRow(row, t)
    })

    this.form.csvHasErrors = this.csvRows.some(row => Object.keys(row.fieldErrors).length > 0)
    this.form.csvBlob = this.csvRows.length ? rowsToCsvText(this.csvRows) : ''
  }
}
